import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Input, Modal, Radio, Select, Space, Table, Typography, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";

import { formatScheduleDraftError } from "../lib/errorUtils";
import { buildTableImportModelFromExcelFile, buildTableImportModelFromHtml, buildTableImportModelFromPastedText } from "../lib/tableImportUtils";
import { getDefaultEditingTrack, getEditingTracks } from "../lib/trackUtils";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { buildScheduleDraftTableImportPayload } from "../services/scheduleDraftPayloadBuilders";

const { Dragger } = Upload;
const { Link, Paragraph, Text } = Typography;

function buildEditingTrackOptions(draft) {
    const editingTracks = getEditingTracks(draft);
    return editingTracks.map((track) => ({
        label: track.name || "未命名编辑轨",
        value: track.id,
    }));
}

function resolveDefaultTargetTrackId(draft, currentEditingTrackId) {
    const editingTracks = getEditingTracks(draft);
    const currentTrack = editingTracks.find((track) => String(track.id) === String(currentEditingTrackId));
    return currentTrack?.id || getDefaultEditingTrack(draft)?.id || draft?.default_track_id || undefined;
}

function findTrackById(draft, trackId) {
    if (trackId === undefined || trackId === null || trackId === "") {
        return null;
    }
    return (draft?.tracks || []).find((track) => String(track.id) === String(trackId)) || null;
}

function hasLockedTargetTrack(lockedTargetTrackId) {
    return lockedTargetTrackId !== undefined && lockedTargetTrackId !== null && lockedTargetTrackId !== "";
}

export function buildPreviewSummary(preview) {
    if (!preview) {
        return "";
    }
    if (preview.can_import) {
        return `可导入 ${preview.valid_rows || 0} 个航班，${preview.warning_count || 0} 个警告。`;
    }
    const typedGlobalError = preview.errors?.find((error) => ["api_error", "parse_error", "input_missing"].includes(error?.type) && error?.message)?.message;
    if (typedGlobalError) {
        return typedGlobalError;
    }
    if (preview.error_count > 0) {
        return `存在 ${preview.error_count} 个阻断错误，已禁止导入。`;
    }
    const firstError = preview.errors?.find((error) => error?.message)?.message;
    return firstError || "表格导入预览失败，已禁止导入。";
}

function buildPreviewDescription(preview, mappingText) {
    const globalErrors = (preview?.errors || []).map((error) => error?.message).filter(Boolean);
    if (globalErrors.length) {
        return globalErrors.join("；");
    }
    return mappingText || "未建立字段映射";
}

const previewColumns = [
    { title: "行", dataIndex: "source_row_number", width: 70 },
    { title: "航班号", dataIndex: "flight_number", width: 110 },
    { title: "飞机", dataIndex: "aircraft_reg", width: 120 },
    { title: "DEP", dataIndex: "departure_airport", width: 90 },
    { title: "ARR", dataIndex: "arrival_airport", width: 90 },
    { title: "STD(UTC)", dataIndex: "std", width: 180 },
    { title: "STA(UTC)", dataIndex: "sta", width: 180 },
];

const errorColumns = [
    { title: "行", dataIndex: "row_number", width: 70 },
    {
        title: "错误",
        dataIndex: "errors",
        render: (errors = []) => errors.map((error) => error.message).join("；"),
    },
];

const ScheduleDraftTableImportModal = ({
    open,
    draft,
    currentEditingTrackId,
    onCancel,
    onImportTableFlights,
    importingTableFlights,
    dirtyBlockReason = "",
    initialInputMode = "PASTED_TABLE",
    lockedTargetTrackId = null,
    initialOverrideAircraftReg = "",
}) => {
    const [form] = Form.useForm();
    const [tableModel, setTableModel] = useState(null);
    const [pasteText, setPasteText] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const inputMode = Form.useWatch("inputMode", form);
    const editingTrackOptions = useMemo(() => buildEditingTrackOptions(draft), [draft]);
    const targetTrackLocked = hasLockedTargetTrack(lockedTargetTrackId);
    const lockedTargetTrack = useMemo(() => findTrackById(draft, lockedTargetTrackId), [draft, lockedTargetTrackId]);
    const lockedTargetTrackLabel = lockedTargetTrack?.name || "当前编辑轨";
    const confirmLoading = previewLoading || importingTableFlights;

    useEffect(() => {
        if (!open) {
            return;
        }
        const nextInputMode = initialInputMode === "EXCEL_FILE" ? "EXCEL_FILE" : "PASTED_TABLE";
        form.setFieldsValue({
            inputMode: nextInputMode,
            targetTrackId: targetTrackLocked ? lockedTargetTrackId : resolveDefaultTargetTrackId(draft, currentEditingTrackId),
            overrideAircraftReg: initialOverrideAircraftReg || undefined,
        });
        setTableModel(null);
        setPasteText("");
        setPreview(null);
        setFileList([]);
    }, [currentEditingTrackId, draft, form, initialInputMode, initialOverrideAircraftReg, lockedTargetTrackId, open, targetTrackLocked]);

    const applyTableModel = (model) => {
        setTableModel(model);
        setPreview(null);
    };

    const handlePasteTextChange = (event) => {
        const nextText = event.target.value;
        setPasteText(nextText);
        applyTableModel(buildTableImportModelFromPastedText(nextText));
    };

    const handlePaste = (event) => {
        const html = event.clipboardData?.getData("text/html");
        const htmlModel = buildTableImportModelFromHtml(html);
        if (!htmlModel) {
            return;
        }
        const plainText = event.clipboardData?.getData("text/plain") || "";
        event.preventDefault();
        setPasteText(plainText);
        applyTableModel(htmlModel);
    };

    const handleBeforeUpload = async (file) => {
        try {
            const model = await buildTableImportModelFromExcelFile(file);
            if (!model) {
                message.error("Excel 文件中未找到可读取的工作表");
                return false;
            }
            applyTableModel(model);
            setFileList([file]);
            message.success(`已解析 ${model.rows.length} 行表格数据`);
        } catch (error) {
            message.error(formatScheduleDraftError(error, "解析 Excel 文件失败"));
        }
        return false;
    };

    const buildValues = async () => {
        const values = await form.validateFields();
        const targetTrackId = targetTrackLocked ? lockedTargetTrackId : values.targetTrackId;
        return {
            ...values,
            targetTrackId,
            target_track_id: targetTrackId,
        };
    };

    const handlePreview = async () => {
        if (!draft?.id) {
            message.error("缺少航班计划 ID，无法预览表格导入");
            return null;
        }
        if (tableModel?.parse_error) {
            message.warning(tableModel.parse_error);
            setPreview({ can_import: false, error_count: 0, errors: [{ type: "parse_error", message: tableModel.parse_error }], rows: [], flights: [], field_mapping: [] });
            return null;
        }
        if (!tableModel?.headers?.length || !tableModel?.rows?.length) {
            const messageText = "请先上传 Excel 文件或粘贴包含表头和数据行的表格文本";
            message.warning(messageText);
            setPreview({ can_import: false, error_count: 0, errors: [{ type: "input_missing", message: messageText }], rows: [], flights: [], field_mapping: [] });
            return null;
        }

        setPreviewLoading(true);
        try {
            const values = await buildValues();
            const result = await scheduleDraftDataService.previewTableImport(draft.id, buildScheduleDraftTableImportPayload(values, tableModel));
            setPreview(result);
            if (result?.can_import) {
                message.success(buildPreviewSummary(result));
            } else {
                message.warning(buildPreviewSummary(result));
            }
            return result;
        } catch (error) {
            const errorMessage = formatScheduleDraftError(error, "预览表格导入失败");
            message.error(errorMessage);
            setPreview({ can_import: false, error_count: 0, errors: [{ type: "api_error", message: errorMessage }], rows: [], flights: [], field_mapping: [] });
            return null;
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (dirtyBlockReason) {
            message.warning(dirtyBlockReason);
            return;
        }
        const values = await buildValues();
        const latestPreview = preview || (await handlePreview());
        if (!latestPreview?.can_import) {
            return;
        }
        await onImportTableFlights?.({ values, tableModel });
    };

    const previewFlights = (preview?.flights || []).slice(0, 20).map((flight) => ({ ...flight, key: `${flight.source_row_number}-${flight.flight_number}` }));
    const errorRows = (preview?.rows || []).filter((row) => row.errors?.length).map((row) => ({ ...row, key: row.row_number }));
    const mappingText = (preview?.field_mapping || [])
        .filter((item) => item.mapped)
        .map((item) => `${item.label}: ${item.header}`)
        .join("；");

    return (
        <Modal
            title="表格导入航班"
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText="导入到编辑轨"
            cancelText="取消"
            confirmLoading={confirmLoading}
            width={920}
            destroyOnClose
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {dirtyBlockReason ? <Alert type="warning" showIcon message={dirtyBlockReason} /> : null}

                <Form form={form} layout="vertical">
                    <Form.Item name="inputMode" label="输入方式" rules={[{ required: true, message: "请选择输入方式" }]}>
                        <Radio.Group
                            optionType="button"
                            options={[
                                { label: "粘贴表格", value: "PASTED_TABLE" },
                                { label: "Excel 文件", value: "EXCEL_FILE" },
                            ]}
                            onChange={() => {
                                setTableModel(null);
                                setPreview(null);
                            }}
                        />
                    </Form.Item>

                    {inputMode === "EXCEL_FILE" ? (
                        <Form.Item label="Excel 文件">
                            <Dragger
                                accept=".xlsx,.xls"
                                multiple={false}
                                beforeUpload={handleBeforeUpload}
                                fileList={fileList}
                                onRemove={() => {
                                    setFileList([]);
                                    applyTableModel(null);
                                }}
                            >
                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                <p className="ant-upload-text">点击或拖拽 Excel 文件到此处解析</p>
                                <p className="ant-upload-hint">读取第一个工作表；支持 .xlsx / .xls。</p>
                            </Dragger>
                        </Form.Item>
                    ) : (
                        <Form.Item label="粘贴表格文本">
                            <Input.TextArea
                                rows={8}
                                value={pasteText}
                                onChange={handlePasteTextChange}
                                onPaste={handlePaste}
                                placeholder={"Date\tFlight\tA/C REG\tDEP\tARR\tSTD\tSTA\n2026-05-01\tFM801\tB-1234\tHKG\tLGG\t01:05\t09:05"}
                            />
                        </Form.Item>
                    )}

                    {targetTrackLocked ? (
                        <>
                            <Form.Item name="targetTrackId" hidden rules={[{ required: true, message: "缺少锁定的导入目标编辑轨" }]}>
                                <Input />
                            </Form.Item>
                            <div className="schedule-draft-source-track-note">
                                <Text strong>导入目标编辑轨</Text>
                                <Paragraph type="secondary">{lockedTargetTrackLabel}</Paragraph>
                            </div>
                        </>
                    ) : (
                        <Form.Item name="targetTrackId" label="导入目标编辑轨" rules={[{ required: true, message: "请选择导入目标编辑轨" }]}>
                            <Select showSearch placeholder="选择已有编辑轨" optionFilterProp="label" options={editingTrackOptions} />
                        </Form.Item>
                    )}
                    <Form.Item name="overrideAircraftReg" hidden rules={[{ required: true, message: "缺少导入飞机" }]}>
                        <Input />
                    </Form.Item>
                </Form>

                <Space>
                    <Text type="secondary">已解析：{tableModel?.rows?.length || 0} 行，{tableModel?.headers?.length || 0} 列</Text>
                    <Link onClick={handlePreview}>解析预览</Link>
                </Space>

                {preview ? (
                    <Alert type={preview.can_import ? "success" : "error"} showIcon message={buildPreviewSummary(preview)} description={buildPreviewDescription(preview, mappingText)} />
                ) : null}

                {preview?.unmapped_columns?.length ? (
                    <Paragraph type="secondary">未识别列：{preview.unmapped_columns.join("、")}</Paragraph>
                ) : null}

                {errorRows.length ? (
                    <Table size="small" pagination={false} columns={errorColumns} dataSource={errorRows} scroll={{ y: 180 }} />
                ) : null}

                {previewFlights.length ? (
                    <Table size="small" pagination={false} columns={previewColumns} dataSource={previewFlights} scroll={{ x: 900, y: 220 }} />
                ) : null}
            </Space>
        </Modal>
    );
};

export default ScheduleDraftTableImportModal;
